import {
    EntityType,
    ExportFormat,
    FolderItemRepresentation,
    FolderItemType,
    PermissionMappingType,
    PermissionTypeFilter,
    ShareRepresentation,
    ShareSearchFilter,
    ShareSortOption,
    ShareStatus,
    ShareType,
    UpdateShareData,
} from '@common/api';
import { Button } from '@components/button/Button';
import { FlexCol, FlexGrow, FlexRow } from '@components/layout/Flex';
import { Sidebar, SidebarSection, SidebarTable } from '@components/layout/Sidebar';
import { Loading } from '@components/loading/Loading';
import { TableSheetUpdate } from '@components/table-sheet';
import { BasicTitle, Text } from '@components/typography/Typography';
import { useEntity } from '@data/entity/entity';
import { useFolderEntityKey } from '@features/entity/entityFolder';
import { useFolderItem } from '@data/folder/folderActions';
import { useUpdateUserSettingsActions } from '@data/hooks/currentUser';
import { useShareActions } from '@data/share/shareActions';
import { useShares } from '@data/share/shareQueries';
import { useColumnResize } from '@hooks/useColumnResize';
import useDebounce from '@hooks/useDebounce';
import { useTenantLocalStorage } from '@hooks/useLocalStorage';
import { useI18nContext } from '@i18n/i18n-react';
import { useAuth } from '@providers/AuthProvider';
import { patch, Update } from '@use-gpu/state';
import { addDays, midnightLocal } from '@util/date';
import { useMemo, useState } from 'react';
import { usePermissions } from '../../data/permission/permissionActions';
import { getFullName } from '../../data/user/user';
import { useUserGroupMap } from './hooks/useUserGroupOptions';
import { useUserMap } from './hooks/useUserOptions';
import { useDefaultRestrictions } from './ShareRestrictions';
import { ShareSidebarResizer } from './ShareSidebar.style';
import { ShareTable } from './ShareTable';
import { ShareTableSearch } from './ShareTableSearch';
import { ShareFilter } from './types';
import { downloadFile } from '../../util/helpers';
import { Tabs } from '@components/tabs/Tabs';

const MIN_WIDTH = 600;
const MAX_WIDTH = 1000;
const PAGE_SIZE = 200;

const EMPTY_FILTER: ShareFilter = { userIds: [], userGroupIds: [], publicLink: false };

export const ShareSidebar = () => {
    const folderEntityKey = useFolderEntityKey();
    const folderEntity = useEntity(folderEntityKey);
    const folderItemId = folderEntity?.type === EntityType.Folder ? folderEntity.id : folderEntity?.folder?.id;

    const resolvedItemId = folderItemId ?? null;
    const { data: folderItem } = useFolderItem(resolvedItemId);

    // Resizable sidebar
    const {
        settings: { share_sidebar_width: initialWidth },
    } = useAuth();
    const { setShareSidebarWidth } = useUpdateUserSettingsActions();

    const { width, resizeHandlers } = useColumnResize({
        position: 'right',
        initialWidth,
        onWidthChange: setShareSidebarWidth,
        minWidth: MIN_WIDTH,
        maxWidth: MAX_WIDTH,
    });

    // Do we need a feature wrapper?
    return (
        <>
            <ShareSidebarResizer {...resizeHandlers} />
            <Sidebar style={{ width }} $white>
                {folderItem ? (
                    <ShareSidebarInner folderItem={folderItem} />
                ) : (
                    <FlexRow $minWidth="100%" $height="100%" $align="center" $justify="center">
                        <Loading />
                    </FlexRow>
                )}
            </Sidebar>
        </>
    );
};

type ShareSidebarInnerProps = {
    folderItem: FolderItemRepresentation;
};

export const ShareSidebarInner = (props: ShareSidebarInnerProps) => {
    const { folderItem } = props;

    // users with VIEW access see all shares on the item but cannot change them
    const readOnly = !folderItem.canEdit;

    const { user } = useAuth();
    const { LL } = useI18nContext();
    const TABS = [LL.ALL(), LL.TRACKED(), LL.UNTRACKED()] as const;
    const [page, setPage] = useState(PAGE_SIZE);
    const [tab, setTab] = useTenantLocalStorage<string>(`share-sidebar-tabs-${folderItem.id}`, LL.ALL());
    const defaultFilter: ShareSearchFilter = useMemo(() => {
        const filter: ShareSearchFilter = {
            itemNodeIds: { selectAll: false, blanks: false, values: [folderItem.id] },
            statuses: {
                selectAll: false,
                blanks: false,
                values: [ShareStatus.Shared, ShareStatus.InProgress, ShareStatus.Submitted, ShareStatus.Approved],
            },
        };

        if (tab === LL.TRACKED()) return filter;

        if (tab === LL.ALL()) filter.statuses = { selectAll: true, blanks: true, values: [] };

        if (tab === LL.UNTRACKED()) filter.statuses = { selectAll: false, blanks: true, values: [] };

        return filter;
    }, [LL, tab, folderItem.id]);

    const isSheet = folderItem.type === FolderItemType.Sheet;

    // Persisted filter from localStorage
    const [localFilter, setLocalFilter] = useTenantLocalStorage<ShareSearchFilter>(
        `share-table-filters-${tab}:${folderItem.type}:${folderItem.id}`,
        {}
    );
    const filter = useMemo(() => ({ ...defaultFilter, ...localFilter }), [defaultFilter, localFilter]);
    const hasLocalFilter = !!Object.keys(localFilter).length;

    // Debounce filter to reduce excessive queries
    const activeFilter = useDebounce(filter, 250, [folderItem.id]);

    // Fetch users and groups that have SUPER_USER permission
    const { data: superUserPermissionMappings, isLoading: superUserPermissionsLoading } = usePermissions({
        permissionTypePrefixes: [PermissionTypeFilter.SuperUser],
    });

    const userMap = useUserMap();
    const userGroupMap = useUserGroupMap();

    const superUsersSubtext = useMemo(() => {
        if (superUserPermissionsLoading) {
            return LL.LOADING_SUPER_USERS();
        }
        const parts: string[] = [];
        for (const mapping of superUserPermissionMappings ?? []) {
            if (mapping.type === PermissionMappingType.User) {
                const user = userMap.get(mapping.id);
                if (user === undefined) {
                    parts.push(LL.UNKNOWN_USER());
                } else {
                    parts.push(getFullName(user));
                }
            } else if (mapping.type === PermissionMappingType.UserGroup) {
                const userGroup = userGroupMap.get(mapping.id);
                if (userGroup === undefined) {
                    parts.push(LL.UNKNOWN_GROUP());
                } else {
                    parts.push(userGroup.name);
                }
            } else {
                parts.push(LL.UNKNOWN());
            }
        }
        return `${LL.SUPER_USERS()}: ${parts.join(', ')}`;
    }, [LL, superUserPermissionsLoading, superUserPermissionMappings, userMap, userGroupMap]);

    // Fetch filtered data for table
    const { data: filteredShares, isLoading: filteredLoading } = useShares({
        filter: activeFilter,
        sortBy: [ShareSortOption.Id],
        pagination: { pageIndex: 0, pageSize: page },
    });

    // Fetch all data to populate filter options (e.g. dropdowns)
    const { data: shares, isLoading: dataLoading } = useShares({
        filter: defaultFilter,
        sortBy: [ShareSortOption.Id],
        pagination: { pageIndex: 0, pageSize: page },
    });
    const {
        state: { loading: actionLoading },
        api: { create, updateBatch, _export },
    } = useShareActions();

    const [searchFilter, setSearchFilter] = useState(EMPTY_FILTER);
    const [jumpToRow, setJumpToRow] = useState<number | null>(null);

    const getDefaultRestrictions = useDefaultRestrictions();
    const handleShareAdd = async () => {
        const isTracked = tab === LL.TRACKED();
        await create({
            active: true,
            comment: null,
            approverUserId: isTracked ? user!.id : null,
            expirationTime: { active: false, value: addDays(midnightLocal(), 7) },
            restrictions: getDefaultRestrictions(),
            ...searchFilter,
            approverUserGroupId: null,
            type: isTracked ? ShareType.DataEntry : ShareType.View,
            status: isTracked ? ShareStatus.Shared : null,
            itemNodeId: folderItem.id,
            labelIds: [],
            publicKey: { active: searchFilter.publicLink, value: '00000000-0000-0000-0000-000000000000' },
        });
        if (shares?.length) setJumpToRow(shares.length);
        setSearchFilter(EMPTY_FILTER);
    };

    const handleShareChange = (ops: TableSheetUpdate<ShareRepresentation, Update<ShareRepresentation>>[]) => {
        const updates: UpdateShareData[] = [];
        for (const op of ops) {
            let newRow = op.row;
            for (const update of op.updates) newRow = patch(newRow, update);
            updates.push(newRow);
        }

        updateBatch(updates);
    };

    const handleShareExport = async () => {
        const data = await _export({ filter: activeFilter, format: ExportFormat.Xlsx });
        downloadFile(data, `${folderItem.name} - Shares.xlsx`);
    };

    const handleFilterChange = (update: Update<ShareSearchFilter>) => {
        setLocalFilter((prev) => patch(prev, update));
    };

    const handleFilterReset = () => {
        setLocalFilter({});
    };

    const handleLoadMore = () => {
        setPage((curr) => curr + PAGE_SIZE);
    };

    const title = [LL.SHARE(), folderItem.name].join(' ');
    const sharesLength = shares?.length ?? 0;
    const canLoadMore = sharesLength !== 0 && sharesLength % PAGE_SIZE === 0;

    return (
        <FlexCol $flexGrow={1}>
            <SidebarSection>
                <FlexRow>
                    <BasicTitle style={{ fontSize: '14px', height: 24 }}>{title}</BasicTitle>

                    <FlexGrow />

                    {hasLocalFilter ? (
                        <Button
                            $size="small"
                            title={LL.RESET_FILTERS()}
                            $type="quaternary"
                            onClick={handleFilterReset}
                        />
                    ) : null}

                    {/* Reserve enough space for floating/absolute sidebar toggle buttons */}
                    <div style={{ paddingRight: 100, visibility: 'hidden' }}>{LL.SHARE()}</div>
                </FlexRow>
                <ShareTableSearch
                    value={searchFilter}
                    onChange={setSearchFilter}
                    publicLink={isSheet}
                    adding={actionLoading}
                    exporting={actionLoading}
                    onAdd={readOnly ? undefined : handleShareAdd}
                    onExport={handleShareExport}
                />
                {isSheet && <Tabs tabs={TABS} selected={tab} onSelect={setTab} />}
            </SidebarSection>
            <SidebarTable>
                <ShareTable
                    readOnly={readOnly}
                    hasTracked={tab !== LL.UNTRACKED()}
                    hasUntracked={tab !== LL.TRACKED()}
                    isSheet={isSheet}
                    isLoading={dataLoading || filteredLoading}
                    searchFilter={searchFilter}
                    shares={shares}
                    filteredShares={filteredShares}
                    jumpToRow={jumpToRow}
                    filter={filter}
                    onSheetChange={handleShareChange}
                    onFilter={handleFilterChange}
                    onLoadMore={handleLoadMore}
                    canLoadMore={canLoadMore}
                />
            </SidebarTable>
            <Text $muted style={{ margin: '3px 6px', padding: '3px 6px' }}>
                {superUsersSubtext}
            </Text>
        </FlexCol>
    );
};
